import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  Field,
  LoadingState,
  TextArea,
} from "@odingard/design-system";
import { useAsync } from "../../lib/useAsync";
import { policyApi } from "../../lib/api";

// Token-scoped external reviewer surface. The reviewer is not a Studio user and
// has no session: the review-link token in the URL is the only credential, and
// the backend derives tenant and draft scope from it. Nothing here is
// organization-parameterised by the caller.
export function PolExternalReview() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [comment, setComment] = useState("");
  const [reviewNote, setReviewNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const state = useAsync(async () => (token ? policyApi.externalDraft(token) : null), [token]);

  async function run(label: string, fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(label);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Request failed.");
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <div className="ods-container" style={{ padding: "var(--ods-space-5)" }}>
        <ErrorState title="Invalid review link" description="This link is missing its review token." />
      </div>
    );
  }

  const view = state.data;

  return (
    <div className="ods-container" style={{ padding: "var(--ods-space-5)" }}>
      <div className="page-head">
        <div>
          <h1>Policy review</h1>
          <p>You have been invited to review this draft. Your access is time bound and can be revoked.</p>
        </div>
      </div>

      {state.loading && <LoadingState label="Opening the draft…" />}
      {state.error && <ErrorState title="Review unavailable" description={state.error.message} />}
      {error && <ErrorState description={error} />}
      {notice && (
        <div className="ods-failclosed" role="status" style={{ borderColor: "var(--ods-color-success)" }}>
          {notice}
        </div>
      )}

      {view && (
        <>
          <Card>
            <CardHeader
              title={view.draft.title}
              action={<Badge tone="neutral">{view.reviewer.status}</Badge>}
            />
            <CardBody>
              <ul className="ods-stack" style={{ paddingLeft: 0, listStyle: "none" }}>
                {view.sections.map((s) => (
                  <li key={s.id}>
                    <strong style={{ fontWeight: 600 }}>{s.heading}</strong>
                    <p style={{ margin: "4px 0 0", color: "var(--ods-color-muted)" }}>{s.body}</p>
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>

          {view.reviewer.canComment && (
            <Card>
              <CardHeader title="Add a comment" />
              <CardBody>
                <div className="ods-stack">
                  <Field label="Comment">
                    {(id) => <TextArea id={id} rows={3} value={comment} onChange={(e) => setComment(e.target.value)} />}
                  </Field>
                  <div>
                    <Button
                      size="sm"
                      disabled={busy || !comment.trim()}
                      onClick={() =>
                        run("Comment submitted.", async () => {
                          await policyApi.externalComment(token, comment.trim());
                          setComment("");
                        })
                      }
                    >
                      Submit comment
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          <Card>
            <CardHeader title="Submit your review" />
            <CardBody>
              <div className="ods-stack">
                <Field label="Review note">
                  {(id) => <TextArea id={id} rows={2} value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} />}
                </Field>
                <div className="ods-row" style={{ gap: "var(--ods-space-2)" }}>
                  <Button
                    size="sm"
                    disabled={busy}
                    onClick={() =>
                      run("Review submitted: approve.", async () => {
                        await policyApi.externalSubmit(token, "approve", reviewNote.trim());
                        state.reload();
                      })
                    }
                  >
                    Recommend approval
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={busy}
                    onClick={() =>
                      run("Review submitted: changes requested.", async () => {
                        await policyApi.externalSubmit(token, "changes_requested", reviewNote.trim());
                        state.reload();
                      })
                    }
                  >
                    Request changes
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
}
